/**
 * Copyright Mántum All Rights Reserved.
 */
import { isOnline } from '../online';
import { isPlainObject, merge } from '../helper';

import type { Cache, Parameter, Method, Headers, Result } from '../type';

type Options = {
    body?: BodyInit | Record<string, unknown>;
    cache?: Cache;
    headers?: Headers;
    method: Method;
    parameter?: Parameter;
    result?: Result;
    signal?: AbortSignal;
};

export default async <R>(url: string, options: Options): Promise<R> => {
    if (!isOnline()) {
        throw new Error('You appear to be offline');
    }

    // prettier-ignore
    const { 
        result = 'json' 
    } = options;

    const input = new URL(url);
    if (options?.parameter) {
        const params = options.parameter;
        input.search = new URLSearchParams(
            Object.keys(params)
                .filter(
                    (key) =>
                        params[key] !== undefined && params[key] !== 'undefined'
                )
                .reduce((previous, current) => {
                    return {
                        ...previous,
                        [current]: params[current]
                    };
                }, {})
        ).toString();
    }

    // prettier-ignore
    let headers: Record<string, string> = merge({
        "Accept": 'application/json'
    }, options.headers);

    if (options.body) {
        if (Array.isArray(options.body) || isPlainObject(options.body)) {
            // prettier-ignore
            headers = merge({
                'Content-Type': 'application/json'
            }, headers);
            options.body = JSON.stringify(options.body);
        }
    }

    const request = await window.fetch(input, {
        body: options?.body as BodyInit,
        cache: options?.cache,
        headers,
        method: options?.method,
        signal: options?.signal
    });

    const response = await getResponse(result, request);
    if (!request.ok) {
        throw response;
    }
    return response;
};

async function getResponse(result: Result, request: Response) {
    if (result === 'text') {
        return await request.text();
    }
    if (result === 'blob') {
        return await request.blob();
    }
    return await request.json();
}
